// GanttAlerts.jsx
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { AlertTriangle, X, EyeOff, Eye } from 'lucide-react';
import { dropStagePrzesunZadanieGold } from './DragDrop/dropStagePrzesunZadanieGold';

// --- KONFIGURACJA KOLORÓW ALERTÓW ---
export const ALERT_COLORS = {
  collision: {
    main: '#da811b',
    border: '#da811b',
    title: '#da811b',
    rowBg: 'transparent',
  },
  shipment: {
    main: '#dc2626',      // główny kolor (np. dla przycisku/kafelka na wykresie)
    border: '#dc2626',    // pasek z lewej strony na liście
    title: '#dc2626',     // kolor numeru zamówienia i nazwy firmy
    badgeBg: '#dc2626',   // tło plakietki "PO SPEDYCJI"
    badgeText: '#f3e8ff', // tekst plakietki "PO SPEDYCJI"
    infoText: '#db4b4b',  // tekst ze szczegółami daty spedycji
    rowBg: 'rgba(168, 85, 247, 0.08)', // delikatne podświetlenie wiersza
  },
};

export const ALERT_COLORS_stare = {
  collision: {
    main: '#dc2626',
    border: '#ef4444',
    title: '#f87171',
    rowBg: 'transparent',
  },
  shipment: {
    main: '#9333ea',      // główny kolor (np. dla przycisku/kafelka na wykresie)
    border: '#a855f7',    // pasek z lewej strony na liście
    title: '#c084fc',     // kolor numeru zamówienia i nazwy firmy
    badgeBg: '#6b21a8',   // tło plakietki "PO SPEDYCJI"
    badgeText: '#f3e8ff', // tekst plakietki "PO SPEDYCJI"
    infoText: '#d8b4fe',  // tekst ze szczegółami daty spedycji
    rowBg: 'rgba(168, 85, 247, 0.08)', // delikatne podświetlenie wiersza
  },
};


// --- FUNKCJE POMOCNICZE ---

const czyMajaWspolnyArkusz = (arkuszeA, arkuszeB) => {
  if (!arkuszeA || !arkuszeB) return true;

  const listA = Array.isArray(arkuszeA)
    ? arkuszeA.map(String)
    : String(arkuszeA).split(',').map((s) => s.trim()).filter(Boolean);

  const listB = Array.isArray(arkuszeB)
    ? arkuszeB.map(String)
    : String(arkuszeB).split(',').map((s) => s.trim()).filter(Boolean);

  if (listA.length === 0 || listB.length === 0) return true;

  return listA.some((arkusz) => listB.includes(arkusz));
};

const zawieraElement = (elementyIdText, searchElementId) => {
  if (
    elementyIdText === null ||
    elementyIdText === undefined ||
    searchElementId === null ||
    searchElementId === undefined
  ) {
    return false;
  }
  const target = String(searchElementId).trim();

  if (Array.isArray(elementyIdText)) {
    return elementyIdText.map(String).includes(target);
  }

  const lista = String(elementyIdText)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return lista.includes(target);
};

const formatDateToCustomString = (dateObj) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// --- GŁÓWNA FUNKCJA WYLICZAJĄCA KOLIZJE I PRZEKROCZENIA SPEDYCJI ---

/**
 * Zwraca Mapę kolizji: klucz to stageId, a wartość to:
 * {
 *   requiredStartMs: number | null,
 *   blockingStageId: string | null,
 *   shipmentExceeded: boolean,
 *   shipmentMs: number | null
 * }
 */
export const getAlertStageIds = (stages = [], toleranceMinutes = 0) => {
  const alertMap = new Map();

  if (!Array.isArray(stages) || stages.length === 0) {
    return alertMap;
  }

  const toleranceMs = toleranceMinutes * 60 * 1000;
  const stagesByGroupAndIndex = {};
  const ordersMap = {};

  const registerCollision = (targetStageId, predecessorEndMs, predecessorStageId) => {
    const existing = alertMap.get(targetStageId) || {
      requiredStartMs: null,
      blockingStageId: null,
      shipmentExceeded: false,
      shipmentMs: null,
    };

    if (!existing.requiredStartMs || predecessorEndMs > existing.requiredStartMs) {
      existing.requiredStartMs = predecessorEndMs;
      existing.blockingStageId = predecessorStageId;
    }

    alertMap.set(targetStageId, existing);
  };

  const registerShipmentExceeded = (targetStageId, shipmentMs) => {
    const existing = alertMap.get(targetStageId) || {
      requiredStartMs: null,
      blockingStageId: null,
      shipmentExceeded: false,
      shipmentMs: null,
    };

    existing.shipmentExceeded = true;
    existing.shipmentMs = shipmentMs;

    alertMap.set(targetStageId, existing);
  };

  stages.forEach((stage) => {
    if (
      !stage ||
      stage.typ_grupy == 1 ||
      stage.status == 3 ||
      stage.status == 4 ||
      stage.zamowienie_id === undefined ||
      stage.zamowienie_id === null
    ) {
      return;
    }

    const startMs = new Date(stage.start).getTime();
    const endMs = new Date(stage.end).getTime();
    if (Number.isNaN(startMs) || Number.isNaN(endMs)) return;

    const zamowienieIdStr = String(stage.zamowienie_id);
    const stageId = String(stage.global_id || stage.id);
    const procesNazwaId = Number(stage.proces_nazwa_id);
    const indeks = Number(stage.indeks_procesu ?? stage.indeks ?? stage.krok ?? 0);
    const elementIdStr =
      stage.element_id !== undefined && stage.element_id !== null
        ? String(stage.element_id)
        : null;

    // --- SPRAWDZENIE DATY SPEDYCJI (godz. 06:00 rano, jak na wykresie) ---
    if (stage.data_spedycji) {
      const shipmentDateObj = new Date(stage.data_spedycji);
      if (!Number.isNaN(shipmentDateObj.getTime())) {
        shipmentDateObj.setHours(6, 0, 0, 0);
        const shipmentMs = shipmentDateObj.getTime();

        if (endMs > shipmentMs + toleranceMs) {
          registerShipmentExceeded(stageId, shipmentMs);
        }
      }
    }

    const normalizedStage = {
      stageId,
      zamowienieId: zamowienieIdStr,
      elementIdStr,
      arkuszeId: stage.arkusze_id || stage.arkusz_id || stage.arkusze,
      elementy_id: stage.elementy_id,
      indeks,
      procesNazwaId,
      startMs,
      endMs,
    };

    if (elementIdStr !== null) {
      const groupKey = `elem_${zamowienieIdStr}_${elementIdStr}`;
      if (!stagesByGroupAndIndex[groupKey]) {
        stagesByGroupAndIndex[groupKey] = {};
      }
      if (!stagesByGroupAndIndex[groupKey][indeks]) {
        stagesByGroupAndIndex[groupKey][indeks] = [];
      }
      stagesByGroupAndIndex[groupKey][indeks].push(normalizedStage);
    }

    if (!ordersMap[zamowienieIdStr]) {
      ordersMap[zamowienieIdStr] = {
        oprawaStages: [],
        stagesByElement: {},
      };
    }

    if (procesNazwaId === 6) {
      ordersMap[zamowienieIdStr].oprawaStages.push(normalizedStage);
    } else if (elementIdStr !== null) {
      if (!ordersMap[zamowienieIdStr].stagesByElement[elementIdStr]) {
        ordersMap[zamowienieIdStr].stagesByElement[elementIdStr] = [];
      }
      ordersMap[zamowienieIdStr].stagesByElement[elementIdStr].push(normalizedStage);
    }
  });

  // 1. Kolizje sąsiednich procesów (GanttConnections)
  Object.keys(stagesByGroupAndIndex).forEach((groupKey) => {
    const indexesMap = stagesByGroupAndIndex[groupKey];
    const sortedIndexes = Object.keys(indexesMap)
      .map(Number)
      .sort((a, b) => a - b);

    for (let i = 0; i < sortedIndexes.length - 1; i++) {
      const currentStages = indexesMap[sortedIndexes[i]];
      const nextStages = indexesMap[sortedIndexes[i + 1]];

      currentStages.forEach((fromStage) => {
        nextStages.forEach((toStage) => {
          if (!czyMajaWspolnyArkusz(fromStage.arkuszeId, toStage.arkuszeId)) {
            return;
          }
          if (fromStage.endMs > toStage.startMs + toleranceMs) {
            registerCollision(toStage.stageId, fromStage.endMs, fromStage.stageId);
          }
        });
      });
    }
  });

  // 2. Kolizje na wejściu do Oprawy (GanttOprawaConnections)
  Object.keys(ordersMap).forEach((zamowienieIdStr) => {
    const { oprawaStages, stagesByElement } = ordersMap[zamowienieIdStr];
    if (oprawaStages.length === 0) return;

    Object.keys(stagesByElement).forEach((elemId) => {
      const elemStages = stagesByElement[elemId];
      if (elemStages.length === 0) return;

      const maxIndeks = Math.max(...elemStages.map((s) => s.indeks));
      const lastStages = elemStages.filter((s) => s.indeks === maxIndeks);

      const matchingOprawy = oprawaStages.filter((oprawaStage) =>
        zawieraElement(oprawaStage.elementy_id, elemId)
      );

      lastStages.forEach((lastStage) => {
        matchingOprawy.forEach((oprawaStage) => {
          if (lastStage.endMs > oprawaStage.startMs + toleranceMs) {
            registerCollision(oprawaStage.stageId, lastStage.endMs, lastStage.stageId);
          }
        });
      });
    });
  });

  return alertMap;
};

// --- KOMPONENT PRZYCISKU I LISTY KOLIZJI ---

export const GanttAlerts = ({
  allStagesList = [],
  alertStageIds,
  showAlert,
  setShowAlert,
  procesory = [],
  canDragOnGant,
  refreshGantAll,
  onSelectStage,
  onSelectAndCenterStage,
  buttonClassName = '',
  openUpwards = false, // false = rozwija w dół (górny pasek), true = rozwija w górę (dolny pasek)
}) => {
  const [showErrorList, setShowErrorList] = useState(false);
  const containerRef = useRef(null);

  // Zamykanie listy po kliknięciu poza komponentem
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowErrorList(false);
      }
    };
    if (showErrorList) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showErrorList]);

  // Pełne dane klocków, które mają kolizję lub przekroczoną spedycję
  const alertStagesDetails = useMemo(() => {
    if (!alertStageIds || alertStageIds.size === 0) return [];
    return allStagesList
      .filter((stage) => alertStageIds.has(String(stage.global_id || stage.id)))
      .map((stage) => {
        const collisionInfo = alertStageIds.get(String(stage.global_id || stage.id));
        return {
          ...stage,
          requiredStartMs: collisionInfo?.requiredStartMs ?? null,
          blockingStageId: collisionInfo?.blockingStageId ?? null,
          shipmentExceeded: collisionInfo?.shipmentExceeded ?? false,
          shipmentMs: collisionInfo?.shipmentMs ?? null,
        };
      });
  }, [allStagesList, alertStageIds]);

  // Sprawdzenie, czy wśród alertów jest jakakolwiek kolizja kolejności procesów
  const hasAnyProcessCollision = useMemo(
    () => alertStagesDetails.some((s) => Boolean(s.requiredStartMs)),
    [alertStagesDetails]
  );

  const activeButtonColor =
    alertStagesDetails.length > 0 && !hasAnyProcessCollision
      ? ALERT_COLORS.shipment.main
      : ALERT_COLORS.collision.main;

  const handleFixStageCollision = (stage, e) => {
    if (e) e.stopPropagation();
    if (!stage || !stage.requiredStartMs) return;

    if (canDragOnGant && (!canDragOnGant(stage.proces_nazwa_id) || stage.zablokowany == 1)) {
      alert('Ten etap jest zablokowany lub nie masz uprawnień do jego przesunięcia.');
      return;
    }

    const targetDateObj = new Date(stage.requiredStartMs);
    const formattedDropDate = formatDateToCustomString(targetDateObj);

    if (stage.proces_nazwa_id != 6) {
      dropStagePrzesunZadanieGold(
        stage.global_id,
        null,
        stage.procesor_id,
        formattedDropDate,
        refreshGantAll
      );
    }
  };

  const handleToggleButtonClick = () => {
    if (!showAlert) {
      setShowAlert(true);
      if (alertStageIds.size > 0) {
        setShowErrorList(true);
      }
    } else if (!showErrorList && alertStageIds.size > 0) {
      setShowErrorList(true);
    } else {
      setShowErrorList(false);
      setShowAlert(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className={buttonClassName}
        onClick={handleToggleButtonClick}
        style={{
          backgroundColor: showAlert ? activeButtonColor : '#434343',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          padding: '6px 6px',
        }}
        title="Pokaż kolizje kolejności procesów i przekroczenia spedycji"
      >
        <AlertTriangle size={20} style={{ marginRight: '0px' }} />

        {alertStageIds?.size > 0 && (
          <span
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              color: '#ffffff',
              background: showAlert ? 'transparent' : activeButtonColor,
              padding: '1px 6px',
              borderRadius: '10px',
            }}
          >
            {alertStageIds.size}
          </span>
        )}
      </button>

      {showErrorList && alertStagesDetails.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: openUpwards ? 'auto' : '100%',
            bottom: openUpwards ? '100%' : 'auto',
            right: 0,
            marginTop: openUpwards ? 0 : '6px',
            marginBottom: openUpwards ? '6px' : 0,
            width: '320px',
            maxHeight: '360px',
            backgroundColor: '#1f2937',
            color: '#ffffff',
            border: '1px solid #374151',
            borderRadius: '6px',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5)',
            zIndex: 100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Nagłówek okienka */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 10px',
              backgroundColor: '#374151',
              borderBottom: '1px solid #4b5563',
              fontSize: '12px',
              fontWeight: 600,
            }}
          >
            <span>Kolizje i spedycja ({alertStagesDetails.length})</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {showAlert ? (
                <button
                  onClick={() => {
                    setShowAlert((prev) => !prev);
                  }}
                  title="Wyłącz podświetlanie na wykresie"
                  style={{
                    background: '#4b5563',
                    border: 'none',
                    color: '#e5e7eb',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Eye size={12} />
                  Ukryj kolor
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowAlert((prev) => !prev);
                  }}
                  title="Włącz podświetlanie na wykresie"
                  style={{
                    background: '#4b5563',
                    border: 'none',
                    color: '#e5e7eb',
                    borderRadius: '4px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <EyeOff size={12} />
                  Pokaż kolor
                </button>
              )}

              <button
                onClick={() => setShowErrorList(false)}
                title="Zamknij listę (zostaw podświetlenie)"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#9ca3af',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <X size={15} />
              </button>
            </div>
          </div>

          {/* Lista wierszy z błędami */}
          <div
            style={{
              overflowY: 'auto',
              padding: '6px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            {alertStagesDetails.map((stage) => {
              const targetDateStr = stage.requiredStartMs
                ? new Date(stage.requiredStartMs).toLocaleString('pl-PL', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '';

              const shipmentDateStr = stage.shipmentMs
                ? new Date(stage.shipmentMs).toLocaleString('pl-PL', {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '';

              const isLocked = stage.zablokowany == 1 || stage.proces_nazwa_id == 6;
              const procName =
                procesory.find((p) => p.id == stage.procesor_id)?.nazwa ||
                `Procesor ${stage.procesor_id}`;

              const theme = stage.shipmentExceeded
                ? ALERT_COLORS.shipment
                : ALERT_COLORS.collision;

              return (
                <div
                  key={stage.global_id || stage.id}
                  onClick={() => {
                    onSelectStage?.(stage);
                  }}
                  onDoubleClick={() => {
                    onSelectStage?.(stage);
                    onSelectAndCenterStage?.(stage);
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#374151')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = theme.rowBg)}
                  style={{
                    padding: '6px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    backgroundColor: theme.rowBg,
                    borderLeft: `3px solid ${theme.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background 0.15s',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <strong style={{ color: theme.title }}>
                        {stage.nr}_{String(stage.rok || '').substring(2, 4)} {stage.firma_nazwa}
                      </strong>
                      {stage.shipmentExceeded && (
                        <span
                          style={{
                            backgroundColor: ALERT_COLORS.shipment.badgeBg,
                            color: ALERT_COLORS.shipment.badgeText,
                            fontSize: '9px',
                            fontWeight: 700,
                            padding: '1px 4px',
                            borderRadius: '3px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          PO SPEDYCJI
                        </span>
                      )}
                    </div>

                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {stage.tytul || stage.uwagi || 'Brak tytułu'}
                    </span>

                    <span style={{ color: '#9ca3af', fontSize: '10px' }}>
                      {procName}
                      {stage.requiredStartMs && (
                        <>
                          {' | Koniec poprz.: '}
                          <strong style={{ color: '#fbbf24' }}>{targetDateStr}</strong>
                        </>
                      )}
                    </span>

                    {stage.shipmentExceeded && (
                      <span style={{ color: ALERT_COLORS.shipment.infoText, fontSize: '10px' }}>
                        Spedycja: <strong>{shipmentDateStr}</strong> (koniec:{' '}
                        {new Date(stage.end).toLocaleString('pl-PL', {
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        )
                      </span>
                    )}
                  </div>

                  {/* Przycisk "Napraw" pokazujemy, gdy klocek ma kolizję z poprzednikiem */}
                  {stage.requiredStartMs && (
                    <button
                      onClick={(e) => handleFixStageCollision(stage, e)}
                      disabled={isLocked}
                      title={
                        stage.zablokowany == 1
                          ? 'Klocek jest zablokowany (kłódka)'
                          : stage.proces_nazwa_id == 6
                          ? 'Oprawę należy przesunąć ręcznie na wykresie'
                          : `Przesuń na ${targetDateStr}`
                      }
                      style={{
                        backgroundColor: isLocked ? '#6b7280' : '#2a9d8f',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '10px',
                        fontWeight: 600,
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      Napraw
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(GanttAlerts);